import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Bar, Pie } from 'react-chartjs-2';
import { getBarChartOptions, TRANSCENDENT_CARDS } from '../../constants';
import { getLevelChartDataByCharacter } from '../../utils/deckUtils';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import SectionHeader from './SectionHeader.shadcn';

/**
 * Deck Stats sidebar module - displays deck validation and statistics
 * Using shadcn/ui components with Tailwind
 */
function DeckStats({ 
  isCollapsed, 
  onToggle, 
  deckValidation,
  getLevelChartData,
  getCharacterChartData,
  chartOptions,
  cards,
  t = (key) => null,
  hideHeader = false,
}) {
  const { errors, stats } = deckValidation;
  const [selectedCharacter, setSelectedCharacter] = useState('all');
  
  // Get level chart data - either regular or filtered by character
  const levelData = useMemo(() => {
    if (selectedCharacter === 'all') {
      return getLevelChartDataByCharacter(stats, null);
    }
    return getLevelChartDataByCharacter(stats, selectedCharacter);
  }, [stats, selectedCharacter]);
  
  const dynamicBarChartOptions = useMemo(() => {
    const baseOptions = getBarChartOptions(levelData.maxCount || 0);
    // Enable stacking when showing all characters
    if (selectedCharacter === 'all' && levelData.datasets?.length > 1) {
      return {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 8,
              font: { size: 10 }
            }
          }
        },
        scales: {
          ...baseOptions.scales,
          x: { stacked: true },
          y: { ...baseOptions.scales.y, stacked: true }
        }
      };
    }
    return baseOptions;
  }, [levelData.maxCount, selectedCharacter, levelData.datasets?.length]);
  
  // Get available characters for filter
  const availableCharacters = useMemo(() => {
    return levelData.characters || [];
  }, [levelData.characters]);
  
  // Check for transcendent/semi-transcendent cards in the deck
  const transcendentCardsInDeck = useMemo(() => {
    if (!cards || cards.length === 0) return [];
    return cards.filter(card => {
      const cardNum = (card.cardNumber || card.number || '').toUpperCase();
      const cardKey = Object.keys(TRANSCENDENT_CARDS).find(k => k.toUpperCase() === cardNum);
      if (!cardKey) return false;
      
      const cardInfo = TRANSCENDENT_CARDS[cardKey];
      const cardCount = parseInt(card.count) || 1;
      
      if (cardInfo.type === 'transcendent') {
        return true;
      }
      return cardCount > cardInfo.limit;
    }).map(card => {
      const cardNum = (card.cardNumber || card.number || '').toUpperCase();
      const cardKey = Object.keys(TRANSCENDENT_CARDS).find(k => k.toUpperCase() === cardNum);
      const cardInfo = TRANSCENDENT_CARDS[cardKey];
      // Translate the reason using the translation key, with limit placeholder support
      const translatedReason = t(cardInfo.reasonKey)?.replace('{limit}', cardInfo.limit) || cardInfo.reasonKey;
      return {
        name: card.name || cardNum,
        cardNumber: card.cardNumber || card.number,
        count: parseInt(card.count) || 1,
        reason: translatedReason,
        limit: cardInfo.limit,
        type: cardInfo.type,
        isTranscendent: cardInfo.type === 'transcendent'
      };
    });
  }, [cards, t]);

  return (
    <div className="flex flex-col gap-2 max-w-full">
      {!hideHeader && (
        <SectionHeader
          title={t('sidebar.stats.title') || 'Deck Stats'}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
        />
      )}
      
      {/* Always visible: Card counts and validation status */}
      <div className={cn(
        "p-3 bg-card rounded-md flex flex-col gap-2 max-w-full border-l-[3px]",
        errors.length > 0 ? "border-l-destructive" : "border-l-success"
      )}>
        {/* Total Cards */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {t('sidebar.stats.totalCards') || 'Total Cards:'}
          </span>
          <Badge variant={stats.totalCards > 50 ? 'destructive' : 'outline'}>
            {stats.totalCards}/50
          </Badge>
        </div>
        
        {/* Charts - only when expanded */}
        {!isCollapsed && stats.totalCards > 0 && (
          <>
            {/* Level Distribution Bar Chart */}
            <div className="mt-2 max-w-full">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold">
                  {t('sidebar.stats.levelDistribution') || 'Level Distribution'}
                </p>
                {availableCharacters.length > 1 && (
                  <select
                    value={selectedCharacter}
                    onChange={(e) => setSelectedCharacter(e.target.value)}
                    className="text-xs px-2 py-0.5 rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">{t('sidebar.stats.allCharacters') || 'All'}</option>
                    {availableCharacters.map((char, index) => (
                      <option key={char} value={char}>
                        {char}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className={cn(
                selectedCharacter === 'all' && levelData.datasets?.length > 1 ? "h-[200px]" : "h-[160px]"
              )}>
                <Bar data={levelData} options={dynamicBarChartOptions} />
              </div>
            </div>

            {/* Character Distribution Pie Chart */}
            <div className="mt-2 max-w-full">
              <p className="text-xs font-semibold mb-1">
                {t('sidebar.stats.characterDistribution') || 'Character Distribution'}
              </p>
              <div className="h-[160px]">
                <Pie data={getCharacterChartData()} options={chartOptions} />
              </div>
            </div>
          </>
        )}

        {stats.sceneCards > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('sidebar.stats.sceneCards') || 'Scene Cards:'}
            </span>
            <Badge variant="outline">{stats.sceneCards}</Badge>
          </div>
        )}
      </div>

      {/* Always visible: Errors and Warnings */}
      <>
        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="p-3 bg-card rounded-md border-l-[3px] border-l-destructive space-y-2">
            <div className="flex items-center gap-1 text-sm font-semibold text-destructive">
              <XCircle className="h-4 w-4" />
              {t('sidebar.stats.deckErrors') || 'Deck Errors:'}
            </div>
            {errors.map((error, idx) => (
              <div key={idx} className="text-xs p-2 bg-destructive/10 rounded text-destructive">
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Transcendent Cards Warning */}
        {transcendentCardsInDeck.length > 0 && (
          <div className="p-3 bg-card rounded-md border-l-[3px] border-l-warning space-y-2">
            <div className="flex items-center gap-1 text-sm font-semibold text-warning">
              <AlertTriangle className="h-4 w-4" />
              {t('sidebar.stats.transcendentCards') || 'Transcendent Cards:'}
            </div>
            {transcendentCardsInDeck.map((card, idx) => (
              <div 
                key={idx} 
                className={cn(
                  "text-xs p-2 rounded",
                  card.isTranscendent ? "bg-purple-100 dark:bg-purple-900/30" : "bg-warning/10"
                )}
              >
                <strong>{card.cardNumber}</strong> - {card.reason}
              </div>
            ))}
          </div>
        )}

        {/* Success Message */}
        {errors.length === 0 && transcendentCardsInDeck.length === 0 && stats.totalCards > 0 && (
          <div className="flex items-center gap-2 p-3 bg-success/10 rounded-md text-success text-sm">
            <CheckCircle2 className="h-4 w-4" />
            {t('sidebar.stats.deckValid') || 'Deck is valid!'}
          </div>
        )}
        
        {errors.length === 0 && transcendentCardsInDeck.length > 0 && stats.totalCards > 0 && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t('sidebar.stats.deckValidWithTranscendent') || 'Deck is valid but contains transcendent cards'}
            </AlertDescription>
          </Alert>
        )}
      </>
    </div>
  );
}

DeckStats.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  deckValidation: PropTypes.shape({
    errors: PropTypes.array.isRequired,
    stats: PropTypes.shape({
      totalCards: PropTypes.number.isRequired,
      cardsByLevel: PropTypes.object,
      sceneCards: PropTypes.number,
      characterBreakdown: PropTypes.object,
    }).isRequired,
  }).isRequired,
  getLevelChartData: PropTypes.func.isRequired,
  getCharacterChartData: PropTypes.func.isRequired,
  chartOptions: PropTypes.object.isRequired,
  t: PropTypes.func,
  cards: PropTypes.array,
  hideHeader: PropTypes.bool,
};

export default DeckStats;
